import pick from 'lodash/pick';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { currentUserShowSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import { denormalisedResponseEntities } from '../../util/data';
import {
  alternativePayoutFromFormValues,
  ALTERNATIVE_PAYOUT_UPDATE_QUERY,
  buildAlternativePayoutProfileUpdate,
  buildUserStripePayoutChoiceUpdate,
  formatAlternativePayoutForEmail,
} from '../../util/alternativePayout';
import { notifyAlternativePayoutApi } from '../../util/api';
import * as log from '../../util/log';
import { showToast } from '../../util/toast';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/StripePayoutPage/SET_INITIAL_VALUES';
export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_ERROR';
export const SAVE_PAYOUT_DETAILS_IDLE = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_IDLE';

// ================ Reducer ================ //

const initialState = {
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  fromReturnURL: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };
    case SAVE_PAYOUT_DETAILS_IDLE:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: false };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const savePayoutDetailsRequest = () => ({
  type: SAVE_PAYOUT_DETAILS_REQUEST,
});
export const savePayoutDetailsError = () => ({
  type: SAVE_PAYOUT_DETAILS_ERROR,
});
export const savePayoutDetailsSuccess = () => ({
  type: SAVE_PAYOUT_DETAILS_SUCCESS,
});
export const savePayoutDetailsIdle = () => ({
  type: SAVE_PAYOUT_DETAILS_IDLE,
});

// ================ Thunks ================ //

const saveAlternativePayoutDetails = (values, sdk, dispatch, getState) => {
  const alternativePayout = alternativePayoutFromFormValues(values);
  const previous = getState()?.user?.currentUser?.attributes?.profile?.protectedData
    ?.alternativePayout;
  const isUpdate = !!(previous && values.payoutPath !== 'alternativeClear');
  const isClear = values.payoutPath === 'alternativeClear';

  return sdk.currentUser
    .updateProfile(
      buildAlternativePayoutProfileUpdate(alternativePayout),
      ALTERNATIVE_PAYOUT_UPDATE_QUERY
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      const currentUser = entities[0];
      dispatch(currentUserShowSuccess(currentUser));

      if (isClear) {
        // Clearing must not mark the form "ready" (checkmark CTA).
        dispatch(savePayoutDetailsIdle());
        return currentUser;
      }

      if (values.payoutPath === 'alternative' && alternativePayout) {
        const profile = currentUser?.attributes?.profile || {};
        return notifyAlternativePayoutApi({
          isUpdate,
          displayName: profile.displayName,
          payoutSummary: formatAlternativePayoutForEmail(alternativePayout),
        })
          .catch(notifyError => {
            // Don't fail the save if the operator email fails
            log.error(notifyError, 'alternative-payout-notify-failed', {
              country: alternativePayout.country,
              method: alternativePayout.method,
            });
          })
          .then(() => {
            dispatch(savePayoutDetailsSuccess());
            showToast({ messageKey: 'Toast.alternativePayoutSaved' });
            return currentUser;
          });
      }

      dispatch(savePayoutDetailsSuccess());
      return currentUser;
    })
    .catch(e => {
      log.error(e, 'save-alternative-payout-failed', {
        payoutPath: values.payoutPath,
        country: values.country,
        method: values.payoutMethod,
      });
      dispatch(savePayoutDetailsError());
      throw e;
    });
};

export const savePayoutDetails = (values, isUpdateCall) => (dispatch, getState, sdk) => {
  dispatch(savePayoutDetailsRequest());

  if (values?.payoutPath === 'alternative' || values?.payoutPath === 'alternativeClear') {
    return saveAlternativePayoutDetails(values, sdk, dispatch, getState);
  }

  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      if (!values?.recordUserPayoutChoice) {
        dispatch(savePayoutDetailsSuccess());
        return response;
      }
      return sdk.currentUser
        .updateProfile(buildUserStripePayoutChoiceUpdate(), ALTERNATIVE_PAYOUT_UPDATE_QUERY)
        .then(profileResponse => {
          const entities = denormalisedResponseEntities(profileResponse);
          if (entities.length === 1) {
            dispatch(currentUserShowSuccess(entities[0]));
          }
          dispatch(savePayoutDetailsSuccess());
          return response;
        })
        .catch(choiceError => {
          log.error(choiceError, 'save-user-stripe-choice-failed', {
            country: values.country,
          });
          dispatch(savePayoutDetailsSuccess());
          return response;
        });
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};

export const loadData = () => (dispatch, getState, sdk) => {
  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialValues());

  return dispatch(fetchCurrentUser()).then(response => {
    const currentUser = getState().user.currentUser;
    if (currentUser && currentUser.stripeAccount) {
      dispatch(fetchStripeAccount());
    }
    return response;
  });
};
